import type { AiProofreadingBatchStatus } from "@/lib/api/proofreadingAi";

/** 開始からこの時間までは短い間隔でポーリングする。 */
export const AI_PROOFREADING_POLL_FAST_WINDOW_MS = 30 * 1000;

/** 初回ウィンドウ内の間隔（仕様: 5 秒）。 */
export const AI_PROOFREADING_POLL_FAST_INTERVAL_MS = 5 * 1000;

/** ウィンドウ後の最初の間隔（仕様: 15〜30 秒）。 */
export const AI_PROOFREADING_POLL_BACKOFF_START_MS = 15 * 1000;

/** 指数バックオフの上限。 */
export const AI_PROOFREADING_POLL_MAX_INTERVAL_MS = 60 * 1000;

/**
 * 待ち開始からの経過時間に応じた次のポーリング間隔。
 * 30 秒以内は 5 秒、以降は 15s → 30s → 60s（上限）。
 */
export function nextAiProofreadingPollIntervalMs(
  elapsedMs: number,
  previousIntervalMs: number | null = null,
): number {
  if (elapsedMs < AI_PROOFREADING_POLL_FAST_WINDOW_MS) {
    return AI_PROOFREADING_POLL_FAST_INTERVAL_MS;
  }
  if (
    previousIntervalMs == null
    || previousIntervalMs < AI_PROOFREADING_POLL_BACKOFF_START_MS
  ) {
    return AI_PROOFREADING_POLL_BACKOFF_START_MS;
  }
  return Math.min(previousIntervalMs * 2, AI_PROOFREADING_POLL_MAX_INTERVAL_MS);
}

export type AiProofreadingPollController = {
  stop: () => void;
};

/**
 * バッチ状態をポーリングする。`onStatus` が false を返すと停止する。
 *
 * `skipInitialPoll: true` のときは即時 poll せず、間隔後に最初の poll を行う。
 * 画面リロード時は呼び出し側で先に 1 回 GET してから本関数を使う想定。
 *
 * `pollIntervalMs` を渡すとバックオフせず固定間隔にする（テスト用）。
 */
export function startAiProofreadingBatchPolling(params: {
  poll: () => Promise<AiProofreadingBatchStatus>;
  onStatus: (status: AiProofreadingBatchStatus) => boolean | void;
  onError?: (error: unknown) => void;
  pollIntervalMs?: number;
  skipInitialPoll?: boolean;
}): AiProofreadingPollController {
  let timerId: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let previousIntervalMs: number | null = null;
  const startedAt = Date.now();

  const resolveInterval = () => {
    if (params.pollIntervalMs != null) {
      return params.pollIntervalMs;
    }
    return nextAiProofreadingPollIntervalMs(Date.now() - startedAt, previousIntervalMs);
  };

  const scheduleNext = () => {
    if (stopped) return;
    const interval = resolveInterval();
    previousIntervalMs = interval;
    timerId = setTimeout(runPoll, interval);
  };

  const runPoll = async () => {
    if (stopped) return;
    try {
      const status = await params.poll();
      const shouldContinue = params.onStatus(status);
      if (shouldContinue === false || stopped) {
        return;
      }
      scheduleNext();
    } catch (error) {
      params.onError?.(error);
      scheduleNext();
    }
  };

  if (params.skipInitialPoll) {
    scheduleNext();
  } else {
    runPoll().catch((error) => {
      params.onError?.(error);
    });
  }

  return {
    stop: () => {
      stopped = true;
      if (timerId != null) {
        clearTimeout(timerId);
        timerId = null;
      }
    },
  };
}
